import { useTranslation } from 'react-i18next'
import { Grid, Stack } from '@mui/material'
import { CardAttribute } from '@/components'
import { EntitySoundRecordsProps, ObservationSoundRecords } from '@/features'
import { renderObservedSpecie } from '@/features/observationLogs/ObservationLogsTable/ObservationLogsTableBody'
import { ObservationLog } from '@/types'
import { Dispatch, SetStateAction } from 'react'

export type ObservationDetailsProps = {
  observationLog: ObservationLog
  setUpdatedObservationLog: Dispatch<SetStateAction<ObservationLog>>
} & Pick<
  EntitySoundRecordsProps,
  'updateSoundRecord' | 'handleCloneObservationLog' | 'cloningInProcess'
>

export const ObservationSpecies = ({
  observationLog,
  setUpdatedObservationLog,
  updateSoundRecord,
  handleCloneObservationLog,
  cloningInProcess,
}: ObservationDetailsProps): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  return (
    <>
      {observationLog.specie2 ? (
        [1, 2, 3, 4].map((n) => {
          const specie =
            observationLog[`specie${n === 1 ? '' : n}` as keyof ObservationLog]

          const amount =
            observationLog[`amount${n === 1 ? '' : n}` as keyof ObservationLog]

          const counting_method =
            observationLog[
              `counting_method${n === 1 ? '' : n}` as keyof ObservationLog
            ]

          const recording_number =
            observationLog[
              `recording_number${n === 1 ? '' : n}` as keyof ObservationLog
            ]

          return specie ? (
            <Stack
              borderRadius={2}
              p={4}
              my={2}
              bgcolor="grey.200"
              width="100%"
              key={n}
            >
              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('observation-log.fieldTitles.foundSpecies')}
                    value={<>{specie}</>}
                  />
                </Grid>
                {!!amount && (
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.amount')}
                      value={<>{amount}</>}
                    />
                  </Grid>
                )}
                {!!counting_method && (
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.countingMethod')}
                      value={<>{counting_method}</>}
                    />
                  </Grid>
                )}
                {!!observationLog.recording_number && (
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.recordingNumber')}
                      value={<>{recording_number}</>}
                    />
                  </Grid>
                )}
              </Grid>

              {!!observationLog.sound_recordings?.length && (
                <Grid xs={12} item={true} mt={2}>
                  <CardAttribute
                    title={t(
                      'observation-log.fieldTitles.specieNumberSoundRecords',
                      {
                        number: n,
                      }
                    )}
                    value={
                      <ObservationSoundRecords
                        observationLog={observationLog}
                        setUpdatedObservationLog={setUpdatedObservationLog}
                        updateSoundRecord={updateSoundRecord}
                        handleCloneObservationLog={handleCloneObservationLog}
                        cloningInProcess={cloningInProcess}
                        forSpecieNumber={n}
                      />
                    }
                  />
                </Grid>
              )}
            </Stack>
          ) : null
        })
      ) : (
        <Stack borderRadius={2} p={4} my={2} bgcolor="grey.200" width="100%">
          <Grid xs={12} item={true} container={true} spacing={4}>
            <Grid xs={3} item={true}>
              <CardAttribute
                title={t('observation-log.fieldTitles.foundSpecies')}
                value={renderObservedSpecie(
                  observationLog.specie,
                  null,
                  null,
                  null,
                  observationLog.other_specie,
                  observationLog.unknown_specie
                )}
              />
            </Grid>
            {!!observationLog.amount && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.amount')}
                  value={observationLog.amount}
                />
              </Grid>
            )}
            {!!observationLog.counting_method && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.countingMethod')}
                  value={observationLog.counting_method}
                />
              </Grid>
            )}
            {!!observationLog.recording_number && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.recordingNumber')}
                  value={observationLog.recording_number}
                />
              </Grid>
            )}
          </Grid>
          {!!observationLog.sound_recordings?.length && (
            <Grid xs={12} item={true} mt={2}>
              <CardAttribute
                title={t('observation-log.fieldTitles.specieSoundRecords')}
                value={
                  <ObservationSoundRecords
                    observationLog={observationLog}
                    setUpdatedObservationLog={setUpdatedObservationLog}
                    updateSoundRecord={updateSoundRecord}
                    handleCloneObservationLog={handleCloneObservationLog}
                    cloningInProcess={cloningInProcess}
                  />
                }
              />
            </Grid>
          )}
        </Stack>
      )}
    </>
  )
}
